import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { Banner, Button, Card } from '../../design/components'
import { BattleCard } from '../../domains/live/BattleCard'
import { BattleMap } from '../../domains/live/BattleMap'
import { CastWall } from '../../domains/live/CastWall'
import { GameMasterTools } from '../../domains/live/GameMasterTools'
import { InitiativeStrip } from '../../domains/live/InitiativeStrip'
import { MapLegend } from '../../domains/live/MapLegend'
import { PhaseBar } from '../../domains/live/PhaseBar'
import { useEncounterCards } from '../../domains/encounters/queries'
import {
  useEncounterCard,
  useLiveSession,
  useSessionControls,
  type SessionControls,
} from '../../domains/live/queries'
import { sendPing, useLiveSessionStream } from '../../domains/live/useLiveSession'
import { clamp01 } from '../../domains/live/geometry'
import type { InitiativeEntry, LiveSession } from '../../domains/live/types'
import styles from '../../domains/live/live.module.css'

/** How far an arrow key nudges a selected token, as a fraction of the map. Shift makes it coarse. */
const NUDGE = 0.01
const NUDGE_COARSE = 0.05

/**
 * One live session.
 *
 * **Two layouts, and they are not the same layout at two widths.** A player watches: the stage,
 * and nothing to press. A Game Master on a desktop *works*: the left column is exactly what the
 * watchers see, and the right column is every tool, so the thing being changed and the control
 * that changes it are never on different screens (`docs/web-api/phase-9-kickoff.md` §1, decision
 * 8). The split is CSS at a desktop breakpoint, so a Game Master on a phone falls back to the
 * watchers' single column with the tools beneath — two columns at 390px is two unusable columns.
 *
 * **The stage is what the phase decides**, and that is the rework
 * (`docs/web-api/phase-10-kickoff.md`). Before the encounter it is a wall of faces: who is here,
 * and who the table has met. In the encounter it is the turn order and — if the encounter has a
 * place with a battle map — the map. An encounter with no map is not a broken session; it is a
 * social encounter, or a fight in a room nobody has drawn, and it runs on the same faces and the
 * same order.
 */
export function LiveSessionPage() {
  const params = useParams<{ sessionId: string }>()
  const sessionId = params.sessionId ?? ''

  const query = useLiveSession(sessionId)
  const session = query.data

  const { ended, ping } = useLiveSessionStream(sessionId, query.isSuccess)
  const controls = useSessionControls(sessionId)

  /**
   * What is selected: **one person**, never a token — and one *instance* of them.
   *
   * Every selectable thing on this screen — a wall portrait, a turn-order face, a token, a legend
   * row — is one of the encounter's cast, so one id addresses all four and they stay in step for
   * free. It is the participant id rather than the character id because six zombies share a
   * character and clicking the third one has to mean the third one. The token, where there is a map
   * and they are standing on it, is derived below; holding it as a second piece of state would be
   * two things to keep equal and one of them would drift.
   */
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null)

  const selectParticipant = useCallback((participantId: string | null) => {
    setSelectedParticipantId(participantId)
  }, [])

  const canControl = session?.canControl === true
  const live = session?.status === 'Live'

  const selectedToken =
    session?.tokens.find((token) => token.participantId === selectedParticipantId) ?? null

  const selectedTokenId = selectedToken?.tokenId ?? null

  // The keyboard equivalent of a drag. Not an afterthought: `e2e` runs axe, and a map whose only
  // interaction is a pointer gesture is a map some people cannot use at all.
  useEffect(() => {
    if (!canControl || selectedTokenId === null || session === undefined) return

    const onKey = (event: KeyboardEvent) => {
      const step = event.shiftKey ? NUDGE_COARSE : NUDGE

      const delta =
        event.key === 'ArrowLeft'
          ? { x: -step, y: 0 }
          : event.key === 'ArrowRight'
            ? { x: step, y: 0 }
            : event.key === 'ArrowUp'
              ? { x: 0, y: -step }
              : event.key === 'ArrowDown'
                ? { x: 0, y: step }
                : null

      if (delta === null) return

      const token = session.tokens.find((entry) => entry.tokenId === selectedTokenId)
      if (token === undefined) return

      event.preventDefault()
      controls.moveToken.mutate({
        tokenId: selectedTokenId,
        x: clamp01(token.x + delta.x),
        y: clamp01(token.y + delta.y),
      })
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canControl, selectedTokenId, session, controls.moveToken])

  if (query.isPending) return <Message title="Live" text="Loading…" />

  if (query.isError || session === undefined) {
    return (
      <Message
        title="Live"
        text="No such session, or it is not one you can watch. Sessions belong to a campaign."
      />
    )
  }

  const inEncounter = session.phase === 'InEncounter'

  const reorder = (from: number, to: number) => {
    const next = [...session.initiative]
    const [moved] = next.splice(from, 1)
    if (moved !== undefined) next.splice(to, 0, moved)

    controls.setInitiative.mutate({ entries: next.map(toEntryRequest) })
  }

  /* Everything a watcher sees, in the order they read it. */
  const watched = (
    <>
      <PhaseBar
        phase={session.phase}
        encounterType={session.encounterType}
        canControl={canControl && live}
        busy={controls.setPhase.isPending}
        onChange={(phase) => controls.setPhase.mutate({ phase })}
      />

      {inEncounter && (
        <InitiativeStrip
          entries={session.initiative}
          participants={session.participants}
          currentIndex={session.currentIndex}
          canControl={canControl && live}
          onReorder={reorder}
          onSelect={selectParticipant}
          selectedParticipantId={selectedParticipantId}
          onAdvance={() => controls.advance.mutate()}
          round={session.round}
          busy={controls.advance.isPending || controls.setInitiative.isPending}
        />
      )}

      {/* The map only ever draws in the encounter, and only when there is one. Everywhere else
          the same rectangle is the cast — which is not a fallback but the point: an encounter is
          a set of people first and a set of coordinates only sometimes. */}
      {inEncounter && session.mapImageUrl !== null ? (
        <>
          {/* The map speaks token ids because that is what it moves; everything else on this
              screen speaks participant ids. The translation is here, in one place, and it is a
              lookup rather than a second piece of state — every token is somebody in the cast. */}
          <BattleMap
            mapImageUrl={session.mapImageUrl}
            tokens={session.tokens}
            canControl={canControl && live}
            selectedId={selectedTokenId}
            onSelect={(tokenId) => selectParticipant(participantFor(session, tokenId))}
            onMove={(tokenId, x, y) => controls.moveToken.mutate({ tokenId, x, y })}
            onPing={canControl ? (x, y) => void sendPing(sessionId, x, y) : undefined}
            ping={ping}
          />

          <MapLegend
            tokens={session.tokens}
            selectedId={selectedTokenId}
            onSelect={(tokenId) => selectParticipant(participantFor(session, tokenId))}
            canControl={canControl}
          />
        </>
      ) : (
        <CastWall
          participants={session.participants}
          canControl={canControl}
          selectedId={selectedParticipantId}
          onSelect={selectParticipant}
          emptyText={emptyCastText(session, canControl)}
        />
      )}
    </>
  )

  return (
    <AppShell
      title={session.name}
      subtitle={(session.locationName ?? 'Nowhere in particular').toUpperCase()}
    >
      {(ended || session.status === 'Ended') && (
        <Banner tone="info">
          This session is over. The screen stays here until the Game Master starts another.
        </Banner>
      )}

      {controls.failure !== null && (
        <Banner
          tone="error"
          actions={
            <Button variant="secondary" onClick={controls.dismissFailure}>
              Dismiss
            </Button>
          }
        >
          {controls.failure}
        </Banner>
      )}

      {canControl ? (
        <div className={styles.gmLayout}>
          <div className={styles.gmStage}>{watched}</div>
          {/* Two sections, in this order: tools on top, the card beneath. The card is the longer
              of the two and the one read continuously; the tools are pressed and left alone, so
              they take the fixed height at the top. */}
          <div className={styles.gmTools}>
            <GameMasterTools
              session={session}
              controls={controls}
              selectedParticipantId={selectedParticipantId}
              onSelectParticipant={selectParticipant}
              onReorder={reorder}
            />

            <FocusedCard
              session={session}
              controls={controls}
              selectedParticipantId={selectedParticipantId}
            />
          </div>
        </div>
      ) : (
        <>
          {watched}

          <p className={styles.note}>
            The Game Master moves everything here. Pan and zoom are yours alone — nobody else&apos;s
            screen follows.
          </p>

          {/* The turn order is the strip above the map, and it was under the map as well. One is
              enough — and the space is better spent on the thing a player actually needs, which
              is their own numbers. */}
          <YourCard session={session} />

          <Link to="/live">
            <Button variant="secondary" fullWidth>
              All live sessions
            </Button>
          </Link>
        </>
      )}
    </AppShell>
  )
}

/** The wire shape of one order row: a participant. Never a position on a map. */
function toEntryRequest(entry: InitiativeEntry) {
  return { participantId: entry.participantId, value: entry.value, hasActed: entry.hasActed }
}

/** Whose token that is. Every token is somebody in the cast, so this only misses a stale id. */
function participantFor(session: LiveSession, tokenId: string | null): string | null {
  return session.tokens.find((token) => token.tokenId === tokenId)?.participantId ?? null
}

/**
 * What an empty stage says.
 *
 * Four different sentences, because "nobody here" means four different things depending on who is
 * reading and how far along the evening is — and a screen that says the next thing to do is worth
 * four sentences.
 */
function emptyCastText(session: LiveSession, canControl: boolean): string {
  if (!canControl) {
    return session.phase === 'PreEncounter'
      ? 'Nobody has been introduced yet. The Game Master will show you who is here.'
      : 'Nobody to see yet. The Game Master will bring people in.'
  }

  if (session.participants.length === 0) {
    return 'Nobody is in this encounter. Put people on a side from the Encounter tab, and their faces appear here.'
  }

  return 'Everyone in this encounter is out of sight. Introduce somebody from the Cast tab.'
}

/**
 * A player's own card.
 *
 * Which character is theirs is **the server's answer** — `yourCharacterId` on the session, derived
 * per reader exactly as `canControl` is. The client does not cross-reference its own roster against
 * the sides, and does not need to know who it is to know which numbers are its own.
 *
 * A player in an encounter none of their characters is in gets nothing here, which is correct:
 * there are no numbers of theirs to show.
 */
function YourCard({ session }: { session: LiveSession }) {
  const mine = session.yourCharacterId
  const card = useEncounterCard(session.encounterId, mine)

  if (mine === null) return null

  return (
    <Card variant="bio" label="Your character" hint={card.data?.name}>
      {card.isPending ? (
        <p className={styles.note}>Reading your sheet…</p>
      ) : card.isError || card.data === undefined ? (
        <p className={styles.note}>Could not read your card.</p>
      ) : (
        <BattleCard card={card.data} sessionId={session.sessionId} lens={session.encounterType} />
      )}
    </Card>
  )
}

/**
 * The card for whoever is acting.
 *
 * The selected participant if there is one, otherwise **whoever's turn it is** — which is the
 * answer that makes this useful without a click, and the reason the card is on the screen at all.
 *
 * It reads a participant rather than a token, so it works in the half of the evening where there
 * is no map and in the kind of encounter where there never will be one.
 *
 * **The card is per character, and the selection is per instance.** Every zombie has the same
 * numbers, because they are the same sheet; what differs is only which of them the Game Master is
 * pointing at. So the participant is what is selected and the character is what is looked up, and
 * the heading says "Zombie 2" while the card underneath is the zombie's.
 *
 * **Except the stress, which is the one thing on the card that is a copy's own.** A shared sheet
 * cannot say which of six zombies is nearly out, so for a clone the ticked boxes come off the
 * participant and a press writes to the session rather than to the character. Everybody else is
 * handed no `instanceStress` at all, and their card goes on writing to their sheet — which it has
 * to, because a player's stress track outlives the evening.
 */
function FocusedCard({
  session,
  controls,
  selectedParticipantId,
}: {
  session: LiveSession
  controls: SessionControls
  selectedParticipantId: string | null
}) {
  const cards = useEncounterCards(session.encounterId, true)

  const current = session.initiative[session.currentIndex]

  const participantId = selectedParticipantId ?? current?.participantId ?? null

  const focus = session.participants.find(
    (participant) => participant.participantId === participantId,
  )

  const characterId = focus?.characterId ?? null

  const card = (cards.data?.sections.cards?.cards ?? []).find(
    (entry) => entry.characterId === characterId,
  )

  return (
    <Card
      variant="bio"
      label={session.encounterType === 'Social' ? 'Social' : 'Combat'}
      hint={focus?.name ?? 'Nobody'}
    >
      {participantId === null ? (
        <p className={styles.note}>
          Nobody selected and nobody&rsquo;s turn. Pick a face, or start the round.
        </p>
      ) : cards.isPending ? (
        <p className={styles.note}>Reading the sheets…</p>
      ) : card === undefined ? (
        <p className={styles.note}>No card for {focus?.name ?? 'them'}.</p>
      ) : (
        <BattleCard
          card={card}
          sessionId={session.sessionId}
          lens={session.encounterType}
          {...(focus !== undefined && focus.canClone
            ? {
                instanceStress: {
                  marked: focus.stress,
                  busy: controls.setParticipantStress.isPending,
                  onToggle: (slotId: number, isStressed: boolean) =>
                    controls.setParticipantStress.mutate({
                      participantId: focus.participantId,
                      slotId,
                      isStressed,
                    }),
                },
              }
            : {})}
        />
      )}
    </Card>
  )
}

function Message({ title, text }: { title: string; text: string }) {
  return (
    <AppShell title={title}>
      <Card>
        <p className={styles.note}>{text}</p>
      </Card>
    </AppShell>
  )
}
